import {FormWrapper} from "../utils/form/FormWrapper"
import {
    Exercise,
    ExerciseAttachments,
    ExerciseTableData,
    FailedExercise,
    Method,
    PlanExercise,
    PlanExerciseTableData,
    Stage,
    Useraccount
} from "../../../../types/domain";
import React, {useEffect, useMemo, useState} from "react";
import {ExerciseCreatePanel} from "../utils/ExerciseCreatePanel";
import {generatePlanExerciseTableData, sortPlanExercisesByPosition} from "../utils/helperFunctions";
import {MantineReactTable, MRT_ColumnDef, MRT_Row, useMantineReactTable} from "mantine-react-table";
import {MRT_Localization_DE} from "mantine-react-table/locales/de";
import {ActionIcon, Box, Rating, Select, Tooltip} from "@mantine/core";
import UseraccountService from "../../../../services/useraccount.service";
import ExerciseService from "../../../../services/exercise.service";
import {IconAlertTriangle, IconEye} from "@tabler/icons-react";
import {loadPreviewItems, PreviewItem} from "../../../../types/PreviewTypes";

import "../../../../css/components/ExercisesForm.css"
import "../../../../css/components/Modals.css"
import {ShowAttachmentModal} from "../utils/modal/ShowAttachmentModal";
import MethodService from "../../../../services/method.service";


/**
 * @author KMU
 * Form element to create the new exercises
 */

type ExercisesData = {
    planExercises: Array<PlanExercise> | null
    newExercises: Array<PlanExercise>,
    newAttachments: Array<ExerciseAttachments>
    user: Useraccount
    stages: Array<Stage>
}

//parent property that will be used in this element
type ExercisesFormProps = ExercisesData & {
    updateFields: (fields: Partial<ExercisesData>) => void,
    failedExercises: Array<FailedExercise>,
    dbStages: Array<Stage>
}

export function ExercisesForm({
                                  planExercises, newExercises, newAttachments, user, stages,
                                  updateFields, failedExercises, dbStages
                              }: ExercisesFormProps) {

    const [data, setData] = useState<Array<PlanExerciseTableData>>([]);
    const [allExercises, setAllExercises] = useState<Array<Exercise>>([]);
    const [userExercises, setUserExercises] = useState<Array<Exercise>>([]);
    const [previewAttachments, setPreviewAttachments] = useState<string[] | null>(null);
    const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
    const [previewItems, setPreviewItems] = useState<Array<PreviewItem>>([]);
    const [allMethods, setAllMethods] = useState<Array<Method>>([]);

    useEffect(() => {
        MethodService.getAll()
            .then((response: any) => {
                setAllMethods(response.data);
            });
    }, []);


    //definition of table columns
    const columns = useMemo<MRT_ColumnDef<PlanExerciseTableData>[]>(
        () => [
            {
                accessorKey: 'name',
                header: 'Übung/Tätigkeit'
            },
            {
                accessorKey: 'description',
                header: 'Beschreibung',
                size: 300
            },
            {
                accessorKey: 'method',
                header: 'Methode',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                }

            },
            {
                accessorKey: 'material',
                header: 'Material'
            },
            {
                accessorKey: 'stages',
                header: 'Stadien',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                }
            },
            {
                accessorKey: 'attachments',
                header: 'Anhänge',
                size: 50,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({row}) => {
                    const attachCount = row.original.attachments;
                    return (
                        <>
                            {attachCount > 0 ? (
                                <Tooltip label="Anhänge anzeigen">
                                    <div className="tableAttachment">
                                        <span style={{textAlign: 'center'}}>
                                            {attachCount}
                                        </span>
                                        {row.original.paths.length > 0 ?
                                            <ActionIcon
                                                color="blueish"
                                                onClick={() => {
                                                    setPreviewAttachments(row.original.paths);
                                                    setShowAttachmentModal(true);
                                                }}>
                                                <IconEye/>
                                            </ActionIcon> : <></>
                                        }
                                    </div>
                                </Tooltip>
                            ) : <span>0</span>
                            }
                        </>
                    )
                }
            },
            {
                accessorKey: 'execution',
                header: 'Durchführung',
                size: 80,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({cell, row}) => (
                    <Select
                        data={['Ja', 'Nein']}
                        name="execution-dropdown"
                        value={cell.getValue<string>()}
                        onChange={(selectedValue) => changeExecution(selectedValue!, row.original)}
                        withinPortal
                    />
                ),
            },
            {
                accessorKey: 'interest',
                header: 'Interesse',
                size: 80,
                mantineTableHeadCellProps: {
                    align: 'center',
                },
                mantineTableBodyCellProps: {
                    align: 'center',
                },
                Cell: ({cell, row}) => {
                    return (
                        <Rating
                            value={cell.getValue<number>()}
                            onChange={(newValue) => changeInterest(newValue!, row.original)}
                            readOnly={row.original.execution === 'Nein'}
                            fractions={1}
                            color="yellow"
                        />
                    );
                }
            }
        ], [planExercises]
    );

    /**
     * Called in the beginning and when dependency changes
     */
    useEffect(() => {
        //get user specific exercises
        UseraccountService.getExercises(user.id).then((response: any) => {
            setUserExercises(response.data);
        }).catch((e: Error) => {
            console.log(e);
        });

        //get all exercises
        ExerciseService.getAll().then((response: any) => {
            setAllExercises(response.data);
        }).catch((e: Error) => {
            console.log(e);
        });

        //init with already planed ones
        if (newExercises.length === 0) {
            let newExes: Array<PlanExercise> = [];
            planExercises?.forEach((planExercise) => {
                planExercise.execution = 'Ja';
                newExes.push(planExercise)
            })
            updateFields({newExercises: newExes, planExercises: planExercises});
        } else {
            if (planExercises) {
                let pos = planExercises.length + 1
                let newPlanExes = [...planExercises];
                newExercises.forEach((exercise) => {
                    //save only the new entry
                    if (!newPlanExes.some(e => e.exercise.name === exercise.exercise.name
                        && e.exercise.description === exercise.exercise.description && e.exercise.material === exercise.exercise.material)) {
                        newPlanExes.push({
                            id: 0,
                            exercise: exercise.exercise,
                            attachments: exercise.attachments,
                            execution: 'Ja',
                            interest: undefined,
                            position: pos
                        })
                        pos++;
                    }
                })
                newPlanExes = sortPlanExercisesByPosition(newPlanExes);
                updateFields({planExercises: newPlanExes});
                setData([...generatePlanExerciseTableData(newPlanExes, newAttachments, stages)]);
            }
        }
    }, [newExercises]);

    /**
     * React to preview interaction and set preview files
     */
    useEffect(() => {
        if (!previewAttachments || previewAttachments.length === 0) return;
        async function load() {
            const items = await loadPreviewItems(previewAttachments!);
            setPreviewItems(items);
        }
        load();
    }, [previewAttachments]);

    /**
     * React to preview-modal for better visibility
     */
    useEffect(() => {
        if (showAttachmentModal) {
            setTimeout(() => {
                window.dispatchEvent(new Event("resize"));
            }, 50);
        }
    }, [showAttachmentModal]);

    useEffect(() => {
        // force table to re-evaluate row styles when failedExercises changes
        setData(prev => [...prev]);
    }, [failedExercises]);

    /**
     * Change event of the execution dropdown
     * @param value selected value
     * @param data row data
     */
    function changeExecution(value: string, data: any) {
        if (planExercises) {
            let adaptedPlanExes = [...planExercises];
            let index = adaptedPlanExes.findIndex(e =>
                e.exercise.name === data.name &&
                e.exercise.description === data.description &&
                e.exercise.material === data.material);
            adaptedPlanExes[index].execution = value;
            adaptedPlanExes[index].interest = 0;

            adaptedPlanExes = sortPlanExercisesByPosition(adaptedPlanExes);
            updateFields({planExercises: adaptedPlanExes});

            setData([...generatePlanExerciseTableData(adaptedPlanExes, newAttachments, stages)]);
        }
    }

    /**
     * Change event of the interest dropdown
     * @param value selected value
     * @param data row data
     */
    function changeInterest(value: number, data: any) {
        if (planExercises) {
            let adaptedPlanExes = [...planExercises];
            let index = adaptedPlanExes.findIndex(e =>
                e.exercise.name === data.name &&
                e.exercise.description === data.description &&
                e.exercise.material === data.material);

            adaptedPlanExes[index].interest = value;

            adaptedPlanExes = sortPlanExercisesByPosition(adaptedPlanExes);
            updateFields({planExercises: adaptedPlanExes});

            setData([...generatePlanExerciseTableData(adaptedPlanExes, newAttachments, stages)]);
        }
    }

    /**
     * Reorder exercises according to table data
     * @param tableData exercises in table data form
     */
    function reOrderPlanExercises(tableData: Array<ExerciseTableData>) {
        if (planExercises) {
            let reorderedExes: Array<PlanExercise> = []
            let pos = 1;
            tableData.forEach(row => {
                const planExe = planExercises.find(e => e.exercise.name === row.name
                    && e.exercise.description === row.description && e.exercise.material === row.material)
                if (planExe) {
                    planExe.position = pos;
                    reorderedExes.push(planExe);
                    pos++;
                }
            })

            reorderedExes = sortPlanExercisesByPosition(reorderedExes);

            updateFields({planExercises: reorderedExes});
            setData([...generatePlanExerciseTableData(reorderedExes, newAttachments, stages)]);
        }
    }

    const table = useMantineReactTable<PlanExerciseTableData>({
        columns,
        data,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableSorting: false,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        renderRowActions: ({row}) => {
            const failedEntry = failedExercises.find(f =>
                f.exercise.name === row.original.name &&
                f.exercise.description === row.original.description &&
                f.exercise.material === row.original.material
            );
            return <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: '8px'}}>
                {failedEntry  && (
                <Tooltip label={failedEntry.reason}>
                    <ActionIcon color="red" variant="transparent">
                        <IconAlertTriangle/>
                    </ActionIcon>
                </Tooltip>
                )}
            </Box>;
        },
        localization: MRT_Localization_DE,
        enableRowOrdering: true,
        mantineRowDragHandleProps: {
            onDragEnd: () => {
                const {draggingRow, hoveredRow} = table.getState();
                if (hoveredRow && draggingRow) {
                    data.splice(
                        (hoveredRow as MRT_Row<PlanExerciseTableData>).index,
                        0,
                        data.splice(draggingRow.index, 1)[0],
                    );
                    reOrderPlanExercises(data);
                }
            }
        },
        enableStickyHeader: true,
        mantineTableContainerProps: {sx: {maxHeight: '500px'}},
        enablePagination: false,
        mantineTableBodyRowProps: ({row}) => {
            const isFailed = failedExercises.some(f =>
                f.exercise.name === row.original.name &&
                f.exercise.description === row.original.description &&
                f.exercise.material === row.original.material
            );
            return {
                sx: isFailed ? {
                    outline: '2px solid red',
                    outlineOffset: '-2px'
                } : {}
            };
        }
    });

    return (
        <>
            <ExerciseCreatePanel newExercises={newExercises} allExercises={allExercises} userExercises={userExercises}
                                 newAttachments={newAttachments} updateFields={updateFields}
                                 stages={stages} dbStages={dbStages} allMethods={allMethods}/>
            <hr></hr>
            <FormWrapper title="">
                <div className="exerciseListTable">
                    <h4 className="heading">Durchgeführte Tätigkeiten</h4>
                    <MantineReactTable
                        table={table}
                    />
                </div>
            </FormWrapper>
            <ShowAttachmentModal opened={showAttachmentModal} close={() => {
                setShowAttachmentModal(false);
                setPreviewAttachments(null);
                setPreviewItems([]);
            }} previewItems={previewItems} />
        </>
    )
}